import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { DashboardLayout } from "../../components/DashboardLayout";
import { links } from "./SuperAdminPages";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Building2, User, Stethoscope, Users as UsersIcon, Calendar,
  CreditCard, Activity, FileText, ShieldCheck, Pencil, Pause, Ban,
  RefreshCcw, Trash2, AlertTriangle, Sparkles, Plus, X, Mail, Phone, Globe, MapPin,
  LogIn,
} from "lucide-react";
import { ClinicStatusBadge, RenewalBadge, PaymentStatusBadge } from "./components/ClinicBadges";

const TABS = [
  { key: "profile", label: "Profile", icon: Building2 },
  { key: "subscription", label: "Subscription", icon: CreditCard },
  { key: "users", label: "Users", icon: UsersIcon },
  { key: "doctors", label: "Doctors", icon: Stethoscope },
  { key: "patients", label: "Patients", icon: User },
  { key: "appointments", label: "Appointments", icon: Calendar },
  { key: "revenue", label: "Revenue", icon: CreditCard },
  { key: "activity", label: "Activity", icon: Activity },
  { key: "notes", label: "Internal Notes", icon: FileText },
  { key: "audit", label: "Audit Logs", icon: ShieldCheck },
];

export default function SuperAdminClinicViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("profile");
  const [actionModal, setActionModal] = useState(null);  // { kind, reason, notes, days }
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/clinics/${id}/detail`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load clinic");
    }
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const runStatus = async (kind, reason, notes, extraDays) => {
    setBusy(true);
    try {
      if (kind === "extend_trial") {
        await api.post(`/subscriptions/clinic/${id}/extend-trial`, { extra_days: extraDays || 7, reason });
      } else if (kind === "soft_delete") {
        await api.delete(`/admin/clinics/${id}`);
      } else if (kind === "restore") {
        await api.post(`/admin/clinics/${id}/restore`);
      } else {
        await api.post(`/subscriptions/clinic/${id}/${kind}`, { reason, notes });
      }
      toast.success("Done");
      setActionModal(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
    setBusy(false);
  };

  if (loading || !data) {
    return (
      <DashboardLayout links={links} title="Clinic">
        <div className="mg-card text-center text-slate-400 py-10">Loading…</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout links={links} title={data.name}>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <Link to="/super-admin/clinics" className="text-sm text-mg-medical font-semibold inline-flex items-center gap-1" data-testid="back-to-clinics">
          <ArrowLeft className="w-4 h-4" /> All clinics
        </Link>
        <div className="flex items-center gap-2 flex-wrap">
          <Link to={`/super-admin/clinics/${id}/edit`} className="mg-btn-secondary text-sm" data-testid="clinic-edit-btn">
            <Pencil className="w-4 h-4" /> Edit
          </Link>
          <Link to={`/super-admin/clinics/${id}/subscription`} className="mg-btn-secondary text-sm" data-testid="clinic-change-plan-btn">
            <CreditCard className="w-4 h-4" /> Change Plan
          </Link>
          {data.is_trial && (
            <button onClick={() => setActionModal({ kind: "extend_trial" })} className="mg-btn-secondary text-sm" data-testid="extend-trial-btn">
              <Sparkles className="w-4 h-4" /> Extend Trial
            </button>
          )}
          {data.subscription_status !== "suspended" && data.subscription_status !== "disabled" && data.status !== "deleted" && (
            <button onClick={() => setActionModal({ kind: "suspend" })} className="mg-btn-secondary text-sm" data-testid="suspend-btn">
              <Pause className="w-4 h-4" /> Suspend
            </button>
          )}
          {data.subscription_status !== "disabled" && data.status !== "deleted" && (
            <button onClick={() => setActionModal({ kind: "disable" })} className="mg-btn-secondary text-sm" data-testid="disable-btn">
              <Ban className="w-4 h-4" /> Disable
            </button>
          )}
          {(data.subscription_status === "suspended" || data.subscription_status === "disabled" || data.subscription_status === "cancelled") && data.status !== "deleted" && (
            <button onClick={() => setActionModal({ kind: "reactivate" })} className="mg-btn-secondary text-sm" data-testid="reactivate-btn">
              <RefreshCcw className="w-4 h-4" /> Reactivate
            </button>
          )}
          {data.status !== "deleted" && (
            <button onClick={() => setActionModal({ kind: "soft_delete" })} className="text-sm px-3 py-1.5 rounded-lg border border-red-200 text-red-700 bg-red-50 hover:bg-red-100 inline-flex items-center gap-1.5" data-testid="soft-delete-btn">
              <Trash2 className="w-4 h-4" /> Soft delete
            </button>
          )}
          {data.status === "deleted" && (
            <button onClick={() => setActionModal({ kind: "restore" })} className="mg-btn-primary text-sm" data-testid="restore-btn">
              <RefreshCcw className="w-4 h-4" /> Restore
            </button>
          )}
        </div>
      </div>

      {/* Header card */}
      <div className="mg-card mb-4">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-16 h-16 rounded-2xl bg-mg-medical/10 flex items-center justify-center">
            <Building2 className="w-8 h-8 text-mg-medical" />
          </div>
          <div className="flex-1 min-w-[260px]">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-xl font-extrabold text-mg-navy" data-testid="clinic-detail-name">{data.name}</h2>
              <ClinicStatusBadge status={data.subscription_status || data.status} trial={data.is_trial} trialDays={data.trial_remaining_days} />
              <RenewalBadge days={data.renewal_days_remaining} isTrial={data.is_trial && (data.trial_remaining_days !== null)} />
              <PaymentStatusBadge status={data.payment_status} />
            </div>
            <div className="text-xs text-slate-500 mt-1 font-mono">/{data.slug} • {data.clinic_id}</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-xs">
              <Stat label="Users" value={data.users_count} />
              <Stat label="Doctors" value={data.doctors_count} />
              <Stat label="Patients" value={data.patients_count} />
              <Stat label="Appts this month" value={data.appointments_this_month} />
              <Stat label="Revenue (mo)" value={`₹${(data.revenue_this_month || 0).toLocaleString()}`} />
              <Stat label="Revenue (total)" value={`₹${(data.revenue || 0).toLocaleString()}`} />
              <Stat label="Plan" value={data.plan?.name || "—"} />
              <Stat label="Last active" value={data.last_active_at ? new Date(data.last_active_at).toLocaleDateString() : "—"} />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mg-card overflow-x-auto p-0 mb-4">
        <div className="flex border-b border-slate-100">
          {TABS.map((t) => {
            const I = t.icon;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`px-4 py-3 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 border-b-2 whitespace-nowrap ${tab === t.key ? "border-mg-medical text-mg-medical" : "border-transparent text-slate-500 hover:text-mg-navy"}`}
                data-testid={`tab-${t.key}`}>
                <I className="w-3.5 h-3.5" />{t.label}
              </button>
            );
          })}
        </div>
      </div>

      {tab === "profile" && <ProfileTab data={data} />}
      {tab === "subscription" && <SubscriptionTab data={data} />}
      {tab === "users" && <UsersTab users={data.users} />}
      {tab === "doctors" && <DoctorsTab doctors={data.doctors} />}
      {tab === "patients" && <PatientsTab data={data} />}
      {tab === "appointments" && <AppointmentsTab data={data} />}
      {tab === "revenue" && <RevenueTab data={data} />}
      {tab === "activity" && <ActivityTab activity={data.activity} />}
      {tab === "notes" && <NotesTab clinicId={id} notes={data.notes} onChange={load} />}
      {tab === "audit" && <AuditTab activity={data.activity} />}

      {actionModal && (
        <ActionModal action={actionModal} busy={busy} onClose={() => setActionModal(null)}
          onConfirm={(reason, notes, days) => runStatus(actionModal.kind, reason, notes, days)} />
      )}
    </DashboardLayout>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
      <div className="text-base font-extrabold text-mg-navy mt-0.5">{value}</div>
    </div>
  );
}

function Row({ icon, label, value }) {
  return (
    <div className="flex items-start gap-3 py-2 border-b border-slate-50 last:border-0">
      <div className="text-slate-400 mt-0.5">{icon}</div>
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
        <div className="text-sm text-mg-navy">{value || "—"}</div>
      </div>
    </div>
  );
}

function ProfileTab({ data }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="mg-card">
        <h3 className="font-bold text-mg-navy mb-2">Contact</h3>
        <Row icon={<Mail className="w-4 h-4" />} label="Email" value={data.email} />
        <Row icon={<Phone className="w-4 h-4" />} label="Phone" value={data.phone} />
        <Row icon={<Globe className="w-4 h-4" />} label="Website" value={data.website} />
        <Row icon={<MapPin className="w-4 h-4" />} label="Address" value={data.address} />
      </div>
      <div className="mg-card">
        <h3 className="font-bold text-mg-navy mb-2">Identity</h3>
        <Row label="Clinic ID" value={<code className="text-xs font-mono">{data.clinic_id}</code>} />
        <Row label="Slug" value={`/${data.slug}`} />
        <Row label="Status" value={<ClinicStatusBadge status={data.subscription_status || data.status} trial={data.is_trial} trialDays={data.trial_remaining_days} />} />
        <Row label="Created" value={data.created_at ? new Date(data.created_at).toLocaleString() : "—"} />
        <Row label="Updated" value={data.updated_at ? new Date(data.updated_at).toLocaleString() : "—"} />
      </div>
      {data.description && (
        <div className="mg-card md:col-span-2">
          <h3 className="font-bold text-mg-navy mb-2">Description</h3>
          <p className="text-sm text-slate-600 whitespace-pre-wrap">{data.description}</p>
        </div>
      )}
    </div>
  );
}

function SubscriptionTab({ data }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="mg-card">
        <h3 className="font-bold text-mg-navy mb-2">Current Plan</h3>
        <Row label="Plan" value={data.plan?.name || "—"} />
        <Row label="Visibility" value={data.plan?.visibility} />
        <Row label="Amount" value={data.amount ? `₹${data.amount.toLocaleString()} ${data.currency}` : "—"} />
        <Row label="Billing cycle" value={data.billing_cycle} />
        <Row label="AI enabled" value={data.plan ? "Yes" : "—"} />
      </div>
      <div className="mg-card">
        <h3 className="font-bold text-mg-navy mb-2">Subscription & Renewal</h3>
        <Row label="Status" value={data.subscription_status} />
        <Row label="Trial status" value={data.is_trial ? `Trial • ${data.trial_remaining_days}d left` : "Not on trial"} />
        <Row label="Trial ends" value={data.trial_ends_at ? new Date(data.trial_ends_at).toLocaleString() : "—"} />
        <Row label="Next renewal" value={data.next_renewal_date ? new Date(data.next_renewal_date).toLocaleString() : "—"} />
        <Row label="Last payment" value={data.last_payment_date ? new Date(data.last_payment_date).toLocaleString() : "—"} />
        <Row label="Payment status" value={<PaymentStatusBadge status={data.payment_status} />} />
      </div>
      {(data.plan_change_history || []).length > 0 && (
        <div className="mg-card md:col-span-2">
          <h3 className="font-bold text-mg-navy mb-2">Plan Change History</h3>
          <table className="w-full text-xs">
            <thead className="text-left text-[10px] uppercase tracking-wider text-slate-500">
              <tr><th className="py-2">Date</th><th>From</th><th>To</th><th>Type</th><th>Reason</th></tr>
            </thead>
            <tbody>
              {data.plan_change_history.map((h) => (
                <tr key={h.change_id} className="border-t border-slate-50">
                  <td className="py-2">{new Date(h.created_at).toLocaleDateString()}</td>
                  <td>{h.old_plan_id?.slice(-6) || "—"}</td>
                  <td>{h.new_plan_id?.slice(-6)}</td>
                  <td>{h.change_type}</td>
                  <td className="text-slate-500">{h.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function UsersTab({ users }) {
  const [target, setTarget] = useState(null);
  const [reason, setReason] = useState("");
  const [starting, setStarting] = useState(false);
  const supportedRoles = new Set(["clinic_admin", "doctor", "receptionist", "nurse", "billing_staff", "lab_staff", "support_staff"]);

  const startSupportSession = async () => {
    if (reason.trim().length < 5) return toast.error("Enter a meaningful support reason");
    setStarting(true);
    try {
      await api.post("/admin/impersonate/start", {
        impersonated_user_id: target.user_id,
        reason: reason.trim(),
      });
      toast.success("Support session started");
      const homes = { doctor: "/doctor", receptionist: "/reception" };
      window.location.href = homes[target.role] || "/clinic-admin";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unable to start support session");
      setStarting(false);
    }
  };

  return (
    <>
      <div className="mg-card overflow-x-auto p-0">
        <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
          <tr><th className="px-3 py-3">Name</th><th>Email</th><th>Phone</th><th>Role</th><th>Status</th><th>Last login</th><th>Created</th><th>Support</th></tr>
        </thead>
        <tbody>
          {users.length === 0 && (
            <tr><td colSpan="8" className="px-3 py-8 text-center text-slate-400">No users.</td></tr>
          )}
          {users.map((u) => (
            <tr key={u.user_id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`user-row-${u.user_id}`}>
              <td className="px-3 py-2 font-semibold text-mg-navy">{u.name}</td>
              <td className="text-xs text-slate-600">{u.email}</td>
              <td className="text-xs text-slate-600">{u.phone || "—"}</td>
              <td className="text-xs">{u.role}</td>
              <td className="text-xs"><span className={`mg-status mg-status-${u.status}`}>{u.status}</span></td>
              <td className="text-xs text-slate-500">{u.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : "—"}</td>
              <td className="text-xs text-slate-500">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
              <td>
                {supportedRoles.has(u.role) && u.status === "active" && !u.deleted_at ? (
                  <button
                    type="button"
                    className="text-xs text-mg-medical font-semibold inline-flex items-center gap-1 hover:underline"
                    onClick={() => { setTarget(u); setReason(""); }}
                    data-testid={`support-as-${u.user_id}`}
                  >
                    <LogIn className="w-3.5 h-3.5" /> Access
                  </button>
                ) : <span className="text-xs text-slate-400">Unavailable</span>}
              </td>
            </tr>
          ))}
        </tbody>
        </table>
      </div>
      {target && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="support-session-modal">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h3 className="font-bold text-mg-navy text-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-mg-gold" /> Start support session
            </h3>
            <p className="text-sm text-slate-600 mt-2">
              Continue as <strong>{target.name}</strong> ({target.role}). The session expires after 30 minutes, is fully audited, and blocks security-sensitive account actions.
            </p>
            <label className="text-sm font-medium text-slate-700 mt-4 block" htmlFor="support-reason">Support reason</label>
            <textarea
              id="support-reason"
              className="mg-input mt-1 w-full min-h-24"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Describe the support request or ticket"
              maxLength={500}
              data-testid="support-session-reason"
            />
            <div className="flex gap-2 mt-5">
              <button type="button" onClick={() => setTarget(null)} disabled={starting} className="mg-btn-secondary flex-1 justify-center">Cancel</button>
              <button type="button" onClick={startSupportSession} disabled={starting || reason.trim().length < 5} className="mg-btn-primary flex-1 justify-center" data-testid="confirm-support-session">
                {starting ? "Starting…" : "Start session"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function DoctorsTab({ doctors }) {
  return (
    <div className="mg-card overflow-x-auto p-0">
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
          <tr><th className="px-3 py-3">Name</th><th>Specialization</th><th>Email</th><th>Phone</th><th>Status</th></tr>
        </thead>
        <tbody>
          {doctors.length === 0 && (
            <tr><td colSpan="5" className="px-3 py-8 text-center text-slate-400">No doctors yet.</td></tr>
          )}
          {doctors.map((d) => (
            <tr key={d.user_id} className="border-b border-slate-50">
              <td className="px-3 py-2 font-semibold text-mg-navy">{d.name}</td>
              <td className="text-xs text-slate-600">{d.specialization || "—"}</td>
              <td className="text-xs text-slate-600">{d.email}</td>
              <td className="text-xs text-slate-600">{d.phone || "—"}</td>
              <td className="text-xs"><span className={`mg-status mg-status-${d.status}`}>{d.status}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PatientsTab({ data }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <Card label="Total patients" value={data.patients_count} />
      <Card label="New this month" value={data.patients_new_this_month} />
      <Card label="Active" value="—" hint="Active definition pending" />
      <div className="md:col-span-3 mg-card text-xs text-slate-500">
        Patient-level detail is hidden by default. Use clinic impersonation if support access to individual records is required.
      </div>
    </div>
  );
}

function AppointmentsTab({ data }) {
  const b = data.appointments_breakdown || {};
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
      <Card label="Total" value={data.appointments_count} />
      <Card label="This month" value={data.appointments_this_month} />
      {["scheduled", "confirmed", "checked_in", "completed", "cancelled", "no_show"].map((k) => (
        <Card key={k} label={k.replace(/_/g, " ")} value={b[k] || 0} />
      ))}
    </div>
  );
}

function RevenueTab({ data }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <Card label="Total revenue" value={`₹${(data.revenue || 0).toLocaleString()}`} />
      <Card label="Revenue this month" value={`₹${(data.revenue_this_month || 0).toLocaleString()}`} />
      <Card label="Outstanding" value="—" hint="Awaiting invoice schema" />
      <Card label="Refunds" value="—" hint="Awaiting refund tracking" />
    </div>
  );
}

function Card({ label, value, hint }) {
  return (
    <div className="mg-card">
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
      <div className="text-2xl font-extrabold text-mg-navy mt-1">{value}</div>
      {hint && <div className="text-[10px] text-slate-400 mt-1">{hint}</div>}
    </div>
  );
}

function ActivityTab({ activity }) {
  return (
    <div className="mg-card">
      <ul className="space-y-2">
        {(activity || []).length === 0 && <li className="text-sm text-slate-400 text-center py-4">No recent activity.</li>}
        {(activity || []).map((a) => (
          <li key={a.log_id} className="text-sm flex items-start gap-3 border-b border-slate-50 pb-2 last:border-0">
            <Activity className="w-4 h-4 text-mg-medical mt-0.5" />
            <div className="flex-1">
              <div className="text-mg-navy"><strong>{a.action}</strong> · {a.description || a.entity_type || "—"}</div>
              <div className="text-[10px] text-slate-400">{new Date(a.created_at).toLocaleString()}</div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function AuditTab({ activity }) {
  return <ActivityTab activity={activity} />;
}

function NotesTab({ clinicId, notes, onChange }) {
  const [text, setText] = useState("");
  const [category, setCategory] = useState("support");
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!text.trim()) return toast.error("Note cannot be empty");
    setBusy(true);
    try {
      await api.post(`/admin/clinics/${clinicId}/notes`, { note: text.trim(), category });
      toast.success("Note added");
      setText("");
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const remove = async (note_id) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await api.delete(`/admin/clinics/${clinicId}/notes/${note_id}`);
      toast.success("Deleted");
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="mg-card">
        <h3 className="font-bold text-mg-navy mb-2">Add internal note</h3>
        <div className="flex flex-col gap-2">
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="mg-input" data-testid="note-category">
            <option value="support">Support</option>
            <option value="billing">Billing</option>
            <option value="onboarding">Onboarding</option>
            <option value="risk">Risk</option>
            <option value="other">Other</option>
          </select>
          <textarea className="mg-input min-h-[80px]" value={text} onChange={(e) => setText(e.target.value)}
            placeholder="Internal note visible only to Super Admins…" data-testid="note-text" />
          <div className="text-right">
            <button onClick={add} disabled={busy} className="mg-btn-primary text-sm" data-testid="add-note-btn">
              <Plus className="w-4 h-4" /> Add note
            </button>
          </div>
        </div>
      </div>
      <div className="space-y-2">
        {(notes || []).length === 0 && <div className="mg-card text-sm text-slate-400 text-center">No internal notes yet.</div>}
        {(notes || []).map((n) => (
          <div key={n.note_id} className="mg-card" data-testid={`note-${n.note_id}`}>
            <div className="flex items-start justify-between">
              <div>
                <span className="text-[10px] uppercase tracking-wider font-bold text-mg-medical bg-mg-medical/10 px-2 py-0.5 rounded">{n.category}</span>
                <div className="text-sm text-mg-navy mt-2 whitespace-pre-wrap">{n.note}</div>
                <div className="text-[10px] text-slate-400 mt-2">
                  by {n.created_by_name || n.created_by} · {new Date(n.created_at).toLocaleString()}
                </div>
              </div>
              <button onClick={() => remove(n.note_id)} className="text-red-500 hover:bg-red-50 p-1 rounded" data-testid={`delete-note-${n.note_id}`}>
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ActionModal({ action, busy, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [days, setDays] = useState(7);
  const titles = {
    suspend: "Suspend clinic",
    disable: "Disable clinic",
    reactivate: "Reactivate clinic",
    cancel: "Cancel subscription",
    soft_delete: "Soft-delete clinic",
    restore: "Restore clinic",
    extend_trial: "Extend trial",
  };
  const destructive = ["disable", "soft_delete"].includes(action.kind);
  const needsReason = !["restore", "soft_delete"].includes(action.kind);
  const submit = () => {
    if (needsReason && !reason.trim()) return toast.error("Reason is required");
    onConfirm(reason || "Super admin action", notes, action.kind === "extend_trial" ? Number(days) : undefined);
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full">
        <h3 className="font-bold text-mg-navy text-lg flex items-center gap-2">
          {destructive && <AlertTriangle className="w-5 h-5 text-red-500" />} {titles[action.kind] || action.kind}
        </h3>
        {action.kind === "extend_trial" && (
          <div className="mt-3">
            <label className="text-xs uppercase font-bold tracking-wider text-slate-500">Extra days (1-90)</label>
            <input type="number" min="1" max="90" value={days} onChange={(e) => setDays(e.target.value)} className="mg-input w-full mt-1" data-testid="trial-days-input" />
          </div>
        )}
        {needsReason && (
          <div className="mt-3">
            <label className="text-xs uppercase font-bold tracking-wider text-slate-500">Reason *</label>
            <input value={reason} onChange={(e) => setReason(e.target.value)} className="mg-input w-full mt-1" data-testid="action-reason-input" />
          </div>
        )}
        {!["restore", "soft_delete"].includes(action.kind) && (
          <div className="mt-3">
            <label className="text-xs uppercase font-bold tracking-wider text-slate-500">Notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="mg-input w-full mt-1 min-h-[60px]" data-testid="action-notes-input" />
          </div>
        )}
        {action.kind === "soft_delete" && (
          <p className="text-xs text-red-600 mt-3">This soft-deletes the clinic. It can be restored. Patient/appointment data is preserved.</p>
        )}
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="mg-btn-secondary flex-1 justify-center" data-testid="action-cancel">Cancel</button>
          <button onClick={submit} disabled={busy} className={`flex-1 justify-center ${destructive ? "text-sm px-3 py-1.5 rounded-lg border border-red-200 text-white bg-red-600 hover:bg-red-700 inline-flex items-center gap-1.5" : "mg-btn-primary text-sm"}`} data-testid="action-confirm">
            {busy ? "Working…" : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}
